import { createFileRoute } from "@tanstack/react-router";
import { Flame } from "lucide-react";
import { useEntry, useEntries, useField } from "@/lib/content";

const SITE_URL = "https://tacoya.snapinsolutions.com";

const MENU_ITEMS = [
  { name: "Birria Taco", price: "5.90", desc: "Hand-dipped tortilla, slow-cooked beef, white onion, cilantro. Served with consommé.", section: "Birria — The Original" },
  { name: "Quesabirria", price: "7.50", desc: "Crispy cheese-edge tortilla, melted Oaxaca-style cheese, shredded beef.", section: "Birria — The Original" },
  { name: "Birria Plate", price: "16.90", desc: "Stewed beef, consommé bowl, rice, beans and four hand-pressed tortillas.", section: "Birria — The Original" },
  { name: "Birria Ramen", price: "14.50", desc: "Birria broth, ramen noodles, shredded beef, soft egg, chili oil.", section: "The Fusion" },
  { name: "Birria Burrito", price: "12.90", desc: "Loaded with birria, rice, beans, cheese and salsa verde.", section: "The Fusion" },
  { name: "Loaded Fries", price: "9.90", desc: "Crispy fries, birria, melted cheese, pickled jalapeños, crema.", section: "The Fusion" },
  { name: "Chili Crunch", price: "3.50", desc: "Smoky, crunchy, dangerously good.", section: "Signature Sauces" },
  { name: "Salsa Verde", price: "3.00", desc: "Roasted tomatillos, jalapeño, lime.", section: "Signature Sauces" },
  { name: "Crema", price: "2.50", desc: "Cooling Mexican-style crema.", section: "Signature Sauces" },
];

const menuSections = ["Birria — The Original", "The Fusion", "Signature Sauces"].map((s) => ({
  "@type": "MenuSection",
  name: s,
  hasMenuItem: MENU_ITEMS.filter((i) => i.section === s).map((i) => ({
    "@type": "MenuItem",
    name: i.name,
    description: i.desc,
    offers: { "@type": "Offer", price: i.price, priceCurrency: "EUR" },
  })),
}));

export const Route = createFileRoute("/menu")({
  head: () => ({
    meta: [
      { title: "Menu — Tacoya · Birria, Quesabirria & Ramen" },
      { name: "description", content: "Slow-cooked birria, quesabirria, birria ramen and signature sauces. The Tacoya menu — built for street food culture." },
      { property: "og:title", content: "Tacoya Menu" },
      { property: "og:description", content: "Slow-cooked birria, quesabirria, birria ramen and more." },
      { property: "og:url", content: `${SITE_URL}/menu` },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/menu` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Menu",
          name: "Tacoya Menu",
          url: `${SITE_URL}/menu`,
          inLanguage: ["en", "fi"],
          hasMenuSection: menuSections,
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Menu", item: `${SITE_URL}/menu` },
          ],
        }),
      },
    ],
  }),
  component: MenuPage,
});

function MenuPage() {
  const f = useField();
  const hero = useEntry("menu", "hero");
  const categories = useEntries("menu", "categories");
  const items = useEntries("menu", "items");

  return (
    <div className="relative overflow-hidden">
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 -left-32 w-[520px] h-[520px] rounded-full bg-chili/15 blur-3xl" />
        <div className="absolute top-1/3 -right-40 w-[600px] h-[600px] rounded-full bg-yellow/10 blur-3xl" />
        <div className="absolute inset-0 opacity-[0.02]" style={{ backgroundImage: "linear-gradient(var(--cream) 1px, transparent 1px), linear-gradient(90deg, var(--cream) 1px, transparent 1px)", backgroundSize: "80px 80px" }} />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-5 md:px-8 py-16 md:py-24">
        <header className="mb-16 md:mb-24">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-chili/30 bg-chili/10 backdrop-blur-md mb-6">
            <span className="size-1.5 rounded-full bg-chili animate-pulse" />
            <span className="text-[10px] uppercase tracking-[0.3em] text-chili font-bold">{f(hero, "kicker", "The Menu")}</span>
          </div>
          <h1 className="font-display text-6xl sm:text-7xl md:text-8xl lg:text-[9rem] leading-[1.05] md:leading-[1.02] tracking-tight text-cream">
            {f(hero, "heading_a", "EAT")} <span className="text-yellow">{f(hero, "heading_b", "LIKE")}</span><br /><span className="inline-block mt-2 md:mt-3">{f(hero, "heading_c", "THE STREETS.")}</span>
          </h1>

          <p className="mt-8 max-w-xl text-cream/70 text-base sm:text-lg leading-relaxed">
            {f(hero, "blurb")}
          </p>
        </header>

        <div className="space-y-24 md:space-y-32">
          {categories.map((cat, si) => {
            const catItems = items.filter((i) => i.data.category === cat.key);
            return (
              <section key={cat.key}>
                <div className="flex items-end justify-between gap-4 mb-10">
                  <div>
                    <div className="flex items-center gap-3 mb-3">
                      <span className="font-mono text-[11px] text-yellow font-bold tracking-[0.4em]">
                        {String(si + 1).padStart(2, "0")}
                      </span>
                      <span className="h-px w-12 bg-gradient-to-r from-yellow to-transparent" />
                    </div>
                    <h2 className="font-display text-3xl sm:text-4xl md:text-6xl text-cream leading-none">{f(cat, "title")}</h2>
                  </div>
                  <span className="hidden sm:inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-cream/45 pb-2">
                    <span className="size-1 rounded-full bg-cream/30" />
                    {catItems.length} dishes
                  </span>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {catItems.map((item) => {
                    const name = f(item, "name");
                    const tag = f(item, "tag");
                    const spice = Number(item.data.spice ?? 0);
                    const price = String(item.data.price ?? "");
                    return (
                      <article
                        key={item.key}
                        className="group relative flex flex-col overflow-hidden rounded-3xl border border-cream/10 bg-gradient-to-br from-cream/[0.07] via-cream/[0.03] to-transparent backdrop-blur-2xl shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08),0_30px_60px_-30px_rgba(0,0,0,0.9)] transition-all duration-500 hover:border-yellow/40 hover:-translate-y-1 hover:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.12),0_40px_80px_-30px_rgba(0,0,0,0.95)]"
                      >
                        <div className="relative aspect-[4/3] w-full overflow-hidden">
                          {item.image ? (
                            <img src={item.image} alt={name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-chili/40 via-charcoal to-charcoal flex items-center justify-center">
                              <span className="font-display text-7xl text-yellow/80">{name.charAt(0)}</span>
                            </div>
                          )}
                          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-charcoal/85 via-charcoal/10 to-transparent" />

                          <div className="absolute top-3 left-3 flex items-center gap-2">
                            <span className="flex items-center gap-0.5 px-2 py-1 rounded-full bg-charcoal/70 backdrop-blur-md border border-cream/10">
                              {Array.from({ length: 3 }).map((_, idx) => (
                                <Flame key={idx} size={10} className={idx < spice ? "text-chili fill-chili" : "text-cream/25"} />
                              ))}
                            </span>
                            {tag && (
                              <span className="text-[9px] uppercase tracking-[0.2em] bg-yellow text-charcoal px-2 py-1 font-bold rounded-full shadow-[0_4px_12px_-4px_color-mix(in_oklab,var(--yellow)_60%,transparent)]">
                                {tag}
                              </span>
                            )}
                          </div>

                          <span className="absolute top-3 right-3 font-display text-lg sm:text-xl text-yellow bg-charcoal/75 backdrop-blur-md px-3 py-1 rounded-full border border-yellow/30 shadow-[0_8px_24px_-8px_rgba(0,0,0,0.7)]">
                            €{price}
                          </span>
                        </div>

                        <div className="relative flex flex-col p-5 md:p-6">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="h-px w-6 bg-yellow/60" />
                            <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-yellow/80 font-bold">Dish</span>
                          </div>
                          <h3 className="text-[26px] md:text-[30px] text-cream leading-[1.15] font-extrabold tracking-tight" style={{ fontFamily: "'Poppins', sans-serif" }}>
                            {name}
                          </h3>
                          <span className="mt-2 block h-[3px] w-10 rounded-full bg-gradient-to-r from-chili via-yellow to-transparent" />

                          <p className="mt-4 text-[13px] sm:text-sm text-cream/65 leading-relaxed">
                            {f(item, "desc")}
                          </p>

                          <button
                            type="button"
                            aria-label={`Choose ${name}`}
                            className="mt-6 inline-flex items-center gap-3 self-start group/btn"
                          >
                            <span className="relative inline-flex items-center justify-center size-12 rounded-full bg-gradient-to-br from-chili to-[color-mix(in_oklab,var(--chili)_70%,#ff8a3d)] shadow-[0_10px_30px_-8px_color-mix(in_oklab,var(--chili)_70%,transparent),inset_0_1px_0_0_rgba(255,255,255,0.25)] ring-2 ring-charcoal transition-transform duration-300 group-hover/btn:scale-110 group-hover/btn:rotate-6">
                              <Flame size={20} className="text-charcoal fill-charcoal" />
                              <span className="absolute inset-0 rounded-full bg-chili/40 blur-xl -z-10" />
                            </span>
                            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-cream/70 font-bold group-hover/btn:text-yellow transition-colors">
                              Choose
                            </span>
                          </button>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}
